import React, { useEffect, useState, useCallback } from "react";
import { Shell } from "../components/Shell";
import {
  ConfigMap,
  getAdminConfig,
  saveAdminConfig,
  testLLM,
  listLlmModels,
  testSmtp,
  testDcConnection,
  getWorkspace,
  renameWorkspace,
  WorkspaceInfo,
  AuthError,
  isSuperAdminRole,
  type UserRole,
  type ConfluenceProfile,
  type ConfluenceProfileInput,
  listConfluenceProfiles,
  createConfluenceProfile,
  updateConfluenceProfile,
  deleteConfluenceProfile,
  setDefaultConfluenceProfile,
  testConfluenceProfile,
  type RepoProfile,
  type RepoProfileInput,
  type RepoHost,
  listRepoProfiles,
  createRepoProfile,
  updateRepoProfile,
  deleteRepoProfile,
  setDefaultRepoProfile,
  testRepoProfile,
} from "../lib/auth";
import { useStore } from "../store";

type ProviderId = "claude" | "gemini" | "groq" | "ollama";

/**
 * Execution mode for skill steps. Agentic/hybrid run the skill through a Claude
 * tool-use loop (web + file tools); single-shot is one plain LLM call. Only
 * offered for the Claude provider — see docs/execution-modes-design.md.
 */
type ExecMode = "single-shot" | "agentic" | "hybrid";

interface ProviderDef {
  id: ProviderId;
  label: string;
  company: string;
  letter: string;
  color: string;
  keyName: string;
  modelName: string;
  models: [string, string][];
  /**
   * Self-hosted providers (Ollama): the server URL is the required setting, the
   * API key is optional, and models are listed from the server, not hard-coded.
   */
  selfHosted?: { baseUrlName: string; numCtxName: string };
}

const PROVIDERS: ProviderDef[] = [
  {
    id: "claude",
    label: "Claude",
    company: "Anthropic",
    letter: "C",
    color: "#de350b",
    keyName: "ANTHROPIC_API_KEY",
    modelName: "CLAUDE_MODEL",
    models: [
      ["claude-sonnet-4-6", "Claude Sonnet 4.6 (recommended)"],
      ["claude-opus-4-7", "Claude Opus 4.7"],
      ["claude-haiku-4-5-20251001", "Claude Haiku 4.5 (fast)"],
    ],
  },
  {
    id: "gemini",
    label: "Gemini",
    company: "Google",
    letter: "G",
    color: "#1a73e8",
    keyName: "GEMINI_API_KEY",
    modelName: "GEMINI_MODEL",
    models: [
      ["gemini-2.5-flash", "Gemini 2.5 Flash (recommended)"],
      ["gemini-2.0-flash-lite", "Gemini 2.0 Flash Lite (fast)"],
      ["gemini-2.5-pro", "Gemini 2.5 Pro"],
    ],
  },
  {
    id: "groq",
    label: "Groq",
    company: "Groq",
    letter: "G",
    color: "#f55036",
    keyName: "GROQ_API_KEY",
    modelName: "GROQ_MODEL",
    models: [
      ["llama-3.3-70b-versatile", "Llama 3.3 70B (recommended)"],
      ["openai/gpt-oss-120b", "GPT-OSS 120B"],
    ],
  },
  {
    id: "ollama",
    label: "Ollama",
    company: "Self-hosted",
    letter: "O",
    color: "#3d4a44",
    keyName: "OLLAMA_API_KEY",
    modelName: "OLLAMA_MODEL",
    models: [],
    selfHosted: { baseUrlName: "OLLAMA_BASE_URL", numCtxName: "OLLAMA_NUM_CTX" },
  },
];

const PROVIDER_LABEL: Record<ProviderId, string> = {
  claude: "Claude",
  gemini: "Gemini",
  groq: "Groq",
  ollama: "Ollama",
};

type TabKey =
  | "General"
  | "LLM Providers"
  | "API Credentials"
  | "Email Configurations";
const ALL_TABS: TabKey[] = [
  "General",
  "LLM Providers",
  "API Credentials",
  "Email Configurations",
];
const TEST_PROMPT =
  "Confirm connectivity. What is your model name and what is your current knowledge cutoff date?";

/**
 * Which tabs the caller is allowed to see. Every admin manages their own
 * workspace settings: General (workspace name), LLM keys, and Atlassian/
 * Confluence API credentials — all scoped to their own workspace. SMTP (Email
 * Configurations) is shared platform infrastructure, so it stays super-admin only.
 */
function tabsForRole(role: UserRole | null): TabKey[] {
  if (role && isSuperAdminRole(role)) return ALL_TABS;
  return ["General", "LLM Providers", "API Credentials"];
}

/** True when this workspace has at least one provider API key stored. */
function hasAnyLlmKey(c: ConfigMap): boolean {
  return Boolean(
    c["ANTHROPIC_API_KEY"]?.isSet ||
    c["GEMINI_API_KEY"]?.isSet ||
    c["GROQ_API_KEY"]?.isSet ||
    c["OLLAMA_BASE_URL"]?.isSet,
  );
}

/**
 * Shown in place of tab content when no workspace is active. Settings are
 * workspace-scoped, so there is nothing to load or save until one is selected.
 */
function NoWorkspaceBanner() {
  return (
    <div
      style={{
        margin: "16px 0",
        padding: "12px 16px",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-3)",
        background: "var(--surface-2)",
        color: "var(--ink-muted)",
        fontSize: 13,
      }}
    >
      No workspace selected. Choose a workspace to view and edit its settings.
    </div>
  );
}

export function AdminSettings() {
  const activeEnterpriseId = useStore((s) => s.activeEnterpriseId);
  const [role, setRole] = useState<UserRole | null>(null);
  const [tab, setTab] = useState<TabKey>("General");
  const [config, setConfig] = useState<ConfigMap>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{
    kind: "ok" | "err";
    text: string;
  } | null>(null);

  // editable form state
  const [apiKeys, setApiKeys] = useState<Record<string, string>>({});
  const [models, setModels] = useState<Record<string, string>>({});
  const [defaultProvider, setDefaultProvider] = useState<ProviderId>("claude");
  const [executionMode, setExecutionMode] = useState<ExecMode>("single-shot");
  const [smtp, setSmtp] = useState<Record<string, string>>({});

  const visibleTabs = tabsForRole(role);

  // Stable identity: setToast is guaranteed stable by React, so this closure
  // never needs to change. Recreating it every render made it churn `load`
  // (which depends on it), which churned the fetch effect — an infinite render
  // loop whenever a fetch failed and called flash().
  const flash = useCallback((kind: "ok" | "err", text: string): void => {
    setToast({ kind, text });
    setTimeout(() => setToast(null), 3200);
  }, []);

  const hydrate = useCallback((c: ConfigMap) => {
    setConfig(c);
    const m: Record<string, string> = {};
    for (const p of PROVIDERS) {
      m[p.modelName] = c[p.modelName]?.value || p.models[0]?.[0] || "";
      // Self-hosted fields are plain (non-secret) values saved alongside the
      // model, so they ride in the same map.
      if (p.selfHosted) {
        m[p.selfHosted.baseUrlName] = c[p.selfHosted.baseUrlName]?.value || "";
        m[p.selfHosted.numCtxName] = c[p.selfHosted.numCtxName]?.value || "";
      }
    }
    setModels(m);
    const dp = c["LLM_DEFAULT_PROVIDER"]?.value;
    if (dp === "claude" || dp === "gemini" || dp === "groq" || dp === "ollama")
      setDefaultProvider(dp);
    const em = c["EXECUTION_MODE_DEFAULT"]?.value;
    setExecutionMode(em === "agentic" || em === "hybrid" ? em : "single-shot");
    setSmtp({
      SMTP_HOST: c["SMTP_HOST"]?.value || "",
      SMTP_PORT: c["SMTP_PORT"]?.value || "",
      SMTP_USERNAME: c["SMTP_USERNAME"]?.value || "",
      SMTP_FROM: c["SMTP_FROM"]?.value || "",
      SMTP_PASSWORD: "",
    });
    setApiKeys({});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getAdminConfig();
      setRole(resp.role);
      hydrate(resp.config);
    } catch (e) {
      flash(
        "err",
        e instanceof AuthError ? e.message : "Failed to load settings.",
      );
    } finally {
      setLoading(false);
    }
  }, [hydrate]);

  // Config (LLM keys/models, Atlassian/SMTP creds) is workspace-scoped, so
  // re-fetch whenever the active workspace changes — not just on mount. With no
  // workspace selected the request has nothing to scope to and 400s, so skip it
  // and let the banner below prompt the user instead of looping on the failure.
  useEffect(() => {
    if (!activeEnterpriseId) {
      setLoading(false);
      return;
    }
    void load();
  }, [load, activeEnterpriseId]);

  // Keep the active tab valid: if the role doesn't permit the current tab
  // (e.g. an enterprise admin landed on a hash that pointed at "Email
  // Configurations"), snap back to the first allowed one.
  useEffect(() => {
    if (!visibleTabs.includes(tab)) setTab(visibleTabs[0]);
  }, [visibleTabs, tab]);

  const save = async (): Promise<void> => {
    setSaving(true);
    // Only send values the caller is allowed to change. LLM keys and Atlassian
    // credentials are workspace-scoped — every admin can save their own. SMTP is
    // platform infrastructure, so only super-admins push it (the server 403s the
    // rest anyway).
    const isSuper = role ? isSuperAdminRole(role) : false;
    const values: Record<string, string> = {
      ...models,
      LLM_DEFAULT_PROVIDER: defaultProvider,
      // Agentic/hybrid are Claude-only; any other default provider is forced
      // back to single-shot so a stale agentic setting can't linger.
      EXECUTION_MODE_DEFAULT:
        defaultProvider === "claude" ? executionMode : "single-shot",
    };
    for (const [k, v] of Object.entries(apiKeys))
      if (v.trim()) values[k] = v.trim();
    if (isSuper) {
      for (const [k, v] of Object.entries(smtp)) values[k] = v;
    }
    try {
      const resp = await saveAdminConfig(values);
      setRole(resp.role);
      hydrate(resp.config);
      flash("ok", "Settings saved.");
    } catch (e) {
      flash("err", e instanceof AuthError ? e.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Shell active="settings">
      <div className="oa-root">
        <main className="oa-main oa-main--narrow">
          <h1 className="oa-title">Settings</h1>
          <p className="oa-sub">
            {role && isSuperAdminRole(role)
              ? "Platform infrastructure (SMTP) and per-workspace settings (LLM, Atlassian)."
              : "LLM provider keys and Atlassian credentials for this workspace."}
          </p>

          <div className="oa-tabs">
            {visibleTabs.map((t) => (
              <button
                key={t}
                className={`oa-tab${tab === t ? " oa-tab--active" : ""}`}
                onClick={() => setTab(t)}
              >
                {t}
              </button>
            ))}
          </div>

          {!activeEnterpriseId ? (
            <NoWorkspaceBanner />
          ) : loading ? (
            <div style={{ color: "var(--oa-muted)", padding: 30 }}>
              Loading…
            </div>
          ) : (
            <>
              {tab === "General" && <GeneralTab flash={flash} />}
              {tab === "LLM Providers" && (
                <LlmTab
                  config={config}
                  apiKeys={apiKeys}
                  setApiKeys={setApiKeys}
                  models={models}
                  setModels={setModels}
                  defaultProvider={defaultProvider}
                  setDefaultProvider={setDefaultProvider}
                  executionMode={executionMode}
                  setExecutionMode={setExecutionMode}
                />
              )}
              {tab === "API Credentials" && (
                <>
                  <DcConnectorSection flash={flash} />
                  <ConnectionsTab flash={flash} />
                  <RepoConnectionsTab flash={flash} />
                </>
              )}
              {tab === "Email Configurations" && (
                <EmailTab smtp={smtp} setSmtp={setSmtp} config={config} />
              )}

              {/* The bulk config save applies to the LLM and Email tabs. The
                Connections tab persists each connection through its own CRUD
                actions, and General manages the workspace name itself. */}
              {(tab === "LLM Providers" || tab === "Email Configurations") && (
                <>
                  <div className="oa-save-row">
                    <button
                      className="oa-btn oa-btn--primary"
                      disabled={saving}
                      onClick={() => void save()}
                    >
                      {saving ? "Saving…" : "Save changes"}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </main>

        {toast && (
          <div className={`oa-toast oa-toast--${toast.kind}`}>{toast.text}</div>
        )}
      </div>
    </Shell>
  );
}

/* ------------------------------- General tab ------------------------------- */

/** Workspace display name. Suspend/delete is a super-admin action and lives on
 *  the Enterprises page. */
function GeneralTab({
  flash,
}: {
  flash: (kind: "ok" | "err", text: string) => void;
}) {
  const setSession = useStore((s) => s.setSession);
  const currentUser = useStore((s) => s.currentUser);
  const currentEnterprise = useStore((s) => s.currentEnterprise);
  const activeEnterpriseId = useStore((s) => s.activeEnterpriseId);
  const [workspace, setWorkspace] = useState<WorkspaceInfo | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  // Re-fetch the workspace whenever the active workspace changes. Skip when
  // none is selected — there is nothing to fetch and the request would only
  // error.
  useEffect(() => {
    if (!activeEnterpriseId) return;
    (async (): Promise<void> => {
      try {
        const ws = await getWorkspace();
        setWorkspace(ws);
        setName(ws.name);
      } catch (e) {
        flash(
          "err",
          e instanceof AuthError ? e.message : "Failed to load workspace.",
        );
      }
    })();
  }, [flash, activeEnterpriseId]);

  const save = async (): Promise<void> => {
    if (!name.trim() || !workspace) return;
    setBusy(true);
    try {
      const updated = await renameWorkspace(name.trim());
      setWorkspace(updated);
      // Update the header pill so the new name shows immediately.
      if (currentUser) {
        setSession(currentUser, {
          id: updated.id,
          name: updated.name,
          slug: updated.slug,
          isActive: updated.isActive,
        });
      }
      flash("ok", "Workspace renamed.");
    } catch (e) {
      flash("err", e instanceof AuthError ? e.message : "Rename failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <p className="oa-sub" style={{ marginBottom: 18 }}>
        Your workspace's display name, shown across the admin area.
      </p>
      <div className="oa-section" style={{ maxWidth: 560 }}>
        <div className="oa-section-head">Workspace</div>
        <label className="oa-label" style={{ display: "block" }}>
          Workspace name
        </label>
        <input
          className="oa-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Workspace name"
          style={{ marginTop: 6 }}
        />
        {currentEnterprise && (
          <div
            style={{ marginTop: 12, fontSize: 12 }}
            className="oa-cell-muted"
          >
            Slug: <code>{currentEnterprise.slug}</code>
          </div>
        )}
        <div
          style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}
        >
          <button
            className="oa-btn oa-btn--primary"
            disabled={busy || !name.trim() || name.trim() === workspace?.name}
            onClick={() => void save()}
          >
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </>
  );
}

/* ------------------------------- LLM tab ------------------------------- */

function LlmTab({
  config,
  apiKeys,
  setApiKeys,
  models,
  setModels,
  defaultProvider,
  setDefaultProvider,
  executionMode,
  setExecutionMode,
}: {
  config: ConfigMap;
  apiKeys: Record<string, string>;
  setApiKeys: (
    f: (p: Record<string, string>) => Record<string, string>,
  ) => void;
  models: Record<string, string>;
  setModels: (f: (p: Record<string, string>) => Record<string, string>) => void;
  defaultProvider: ProviderId;
  setDefaultProvider: (p: ProviderId) => void;
  executionMode: ExecMode;
  setExecutionMode: (m: ExecMode) => void;
}) {
  const [output, setOutput] = useState<{
    id: ProviderId;
    text: string;
    err: boolean;
  } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [serverModels, setServerModels] = useState<string[] | null>(null);
  const [loadingModels, setLoadingModels] = useState(false);

  const loadServerModels = async (p: ProviderDef): Promise<void> => {
    if (!p.selfHosted) return;
    setLoadingModels(true);
    setOutput(null);
    try {
      const r = await listLlmModels("ollama", {
        baseUrl: models[p.selfHosted.baseUrlName]?.trim() || undefined,
        apiKey: apiKeys[p.keyName]?.trim() || undefined,
      });
      if (r.ok) {
        const list = r.models ?? [];
        setServerModels(list);
        if (list.length && !models[p.modelName])
          setModels((prev) => ({ ...prev, [p.modelName]: list[0] }));
        setOutput({
          id: p.id,
          text: list.length
            ? `${list.length} model${list.length === 1 ? "" : "s"} on the server.`
            : "The server has no models yet. Pull one with `ollama pull <model>`.",
          err: list.length === 0,
        });
      } else {
        setOutput({ id: p.id, text: r.error || "Could not load models.", err: true });
      }
    } catch (e) {
      setOutput({
        id: p.id,
        text: e instanceof AuthError ? e.message : "Could not load models.",
        err: true,
      });
    } finally {
      setLoadingModels(false);
    }
  };

  const runTest = async (id: ProviderId): Promise<void> => {
    setBusyId(id);
    setOutput(null);
    const def = PROVIDERS.find((p) => p.id === id)!;
    try {
      // Pass the just-typed key + selected model so testing works before saving;
      // blank falls back to the saved config server-side.
      const r = await testLLM(id, {
        message: TEST_PROMPT,
        apiKey: apiKeys[def.keyName]?.trim() || undefined,
        model: models[def.modelName] || undefined,
        baseUrl: def.selfHosted
          ? models[def.selfHosted.baseUrlName]?.trim() || undefined
          : undefined,
      });
      if (r.ok)
        setOutput({ id, text: r.response || "(no response)", err: false });
      else setOutput({ id, text: r.error || "Test failed.", err: true });
    } catch (e) {
      setOutput({
        id,
        text: e instanceof AuthError ? e.message : "Test failed.",
        err: true,
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <p className="oa-sub" style={{ marginBottom: 18 }}>
        Configure API keys and models for each LLM provider.{" "}
        <strong>These settings apply only to this workspace</strong> — every
        workspace manages its own keys and default provider. Keys are stored
        encrypted; only a partial preview is shown after saving.
      </p>
      {!hasAnyLlmKey(config) && (
        <div className="oa-msg oa-msg--err" style={{ marginBottom: 14 }}>
          No LLM API key is configured for this workspace yet. AI features will
          fail until you set one below.
        </div>
      )}
      <div className="oa-provider-grid">
        {PROVIDERS.map((p) => {
          const isDefault = defaultProvider === p.id;
          const set = config[p.keyName]?.isSet;
          return (
            <div
              key={p.id}
              className={`oa-provider-card${isDefault ? " oa-provider-card--default" : ""}`}
              role="radio"
              aria-checked={isDefault}
              tabIndex={0}
              style={{ cursor: "pointer" }}
              onClick={() => setDefaultProvider(p.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setDefaultProvider(p.id);
                }
              }}
            >
              <div className="oa-provider-head">
                <span className="oa-pbadge" style={{ background: p.color }}>
                  {p.letter}
                </span>
                <strong style={{ fontSize: 15 }}>{p.label}</strong>
                <span className="oa-cell-muted" style={{ fontSize: 13 }}>
                  ({p.company})
                </span>
                <span style={{ flex: 1 }} />
                {isDefault && <span className="oa-badge-default">Default</span>}
                <button
                  className="oa-btn oa-btn--sm"
                  disabled={busyId === p.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    void runTest(p.id);
                  }}
                >
                  {busyId === p.id ? "Testing…" : "Test"}
                </button>
              </div>
              {p.selfHosted && (
                <div className="oa-field-row">
                  <span className="oa-label">Base URL</span>
                  <input
                    className="oa-input"
                    type="url"
                    autoComplete="off"
                    placeholder="http://<host>:11434/v1"
                    value={models[p.selfHosted.baseUrlName] ?? ""}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const name = p.selfHosted!.baseUrlName;
                      setModels((prev) => ({ ...prev, [name]: e.target.value }));
                    }}
                  />
                </div>
              )}
              <div className="oa-field-row">
                <span className="oa-label">API Key</span>
                <input
                  className="oa-input"
                  type="password"
                  autoComplete="new-password"
                  placeholder={
                    set
                      ? config[p.keyName].value
                      : p.selfHosted
                        ? "Optional (only needed behind Open WebUI)"
                        : "Enter API key"
                  }
                  value={apiKeys[p.keyName] ?? ""}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) =>
                    setApiKeys((prev) => ({
                      ...prev,
                      [p.keyName]: e.target.value,
                    }))
                  }
                />
              </div>
              {p.selfHosted ? (
                <>
                  <div className="oa-field-row">
                    <span className="oa-label">Model</span>
                    <div style={{ display: "flex", gap: 8, minWidth: 0 }}>
                      <input
                        className="oa-input"
                        list="ollama-server-models"
                        autoComplete="off"
                        placeholder="e.g. qwen3.8:27b"
                        value={models[p.modelName] ?? ""}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) =>
                          setModels((prev) => ({
                            ...prev,
                            [p.modelName]: e.target.value,
                          }))
                        }
                      />
                      <datalist id="ollama-server-models">
                        {(serverModels ?? []).map((m) => (
                          <option key={m} value={m} />
                        ))}
                      </datalist>
                      <button
                        className="oa-btn oa-btn--sm"
                        type="button"
                        disabled={loadingModels}
                        onClick={(e) => {
                          e.stopPropagation();
                          void loadServerModels(p);
                        }}
                      >
                        {loadingModels ? "Loading…" : "Load models"}
                      </button>
                    </div>
                  </div>
                  <div className="oa-field-row">
                    <span className="oa-label">Context (tokens)</span>
                    <input
                      className="oa-input"
                      type="number"
                      min={1024}
                      step={1024}
                      placeholder="Server's OLLAMA_CONTEXT_LENGTH, e.g. 131072"
                      value={models[p.selfHosted.numCtxName] ?? ""}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const name = p.selfHosted!.numCtxName;
                        setModels((prev) => ({ ...prev, [name]: e.target.value }));
                      }}
                    />
                  </div>
                </>
              ) : (
              <div className="oa-field-row">
                <span className="oa-label">Model</span>
                <select
                  className="oa-select"
                  style={{ height: 36 }}
                  value={models[p.modelName] ?? p.models[0][0]}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) =>
                    setModels((prev) => ({
                      ...prev,
                      [p.modelName]: e.target.value,
                    }))
                  }
                >
                  {p.models.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>
              )}
              <label
                className="oa-radio-row"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="radio"
                  name="defaultProvider"
                  checked={isDefault}
                  onChange={() => setDefaultProvider(p.id)}
                />
                Set as default provider
              </label>
              {output?.id === p.id && (
                <span
                  className={`oa-msg ${output.err ? "oa-msg--err" : "oa-msg--ok"}`}
                  style={{ marginTop: 8 }}
                >
                  {output.err ? "✗ " : "✓ "}
                  {output.text}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <ExecutionModeSection
        config={config}
        apiKeys={apiKeys}
        defaultProvider={defaultProvider}
        executionMode={executionMode}
        setExecutionMode={setExecutionMode}
      />
    </>
  );
}

/* ----------------------- Execution mode (Claude) ----------------------- */

const EXEC_MODES: Array<[ExecMode, string, string]> = [
  ["single-shot", "Single-shot (default)", "One LLM call, no tools — the classic path."],
  ["agentic", "Agentic", "Runs the skill through a Claude tool-use loop with web + file tools for exact, sourced facts."],
  ["hybrid", "Hybrid", "Cheap model browses the web, then a strong model synthesizes the artifacts — lower cost."],
];

function ExecutionModeSection({
  config,
  apiKeys,
  defaultProvider,
  executionMode,
  setExecutionMode,
}: {
  config: ConfigMap;
  apiKeys: Record<string, string>;
  defaultProvider: ProviderId;
  executionMode: ExecMode;
  setExecutionMode: (m: ExecMode) => void;
}) {
  const isClaude = defaultProvider === "claude";
  const hasAnthropicKey =
    Boolean(config["ANTHROPIC_API_KEY"]?.isSet) ||
    Boolean(apiKeys["ANTHROPIC_API_KEY"]?.trim());
  const needsKey = isClaude && executionMode !== "single-shot" && !hasAnthropicKey;

  return (
    <div className="oa-provider-card" style={{ marginTop: 18, cursor: "default" }}>
      <div className="oa-provider-head">
        <strong style={{ fontSize: 15 }}>Execution mode</strong>
        <span style={{ flex: 1 }} />
      </div>
      <p className="oa-sub" style={{ marginTop: 4, marginBottom: 12 }}>
        How skill steps run. Agentic and Hybrid are available only with the{" "}
        <strong>Claude</strong> provider and require an Anthropic API key. Other
        providers always run single-shot.
      </p>

      {isClaude ? (
        <>
          <div className="oa-field-row">
            <span className="oa-label">Mode</span>
            <select
              className="oa-select"
              style={{ height: 36 }}
              value={executionMode}
              onChange={(e) => setExecutionMode(e.target.value as ExecMode)}
            >
              {EXEC_MODES.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <p className="oa-cell-muted" style={{ fontSize: 13, marginTop: 8 }}>
            {EXEC_MODES.find(([v]) => v === executionMode)?.[2]}
          </p>
          {needsKey && (
            <span
              className="oa-msg oa-msg--err"
              style={{ marginTop: 8, display: "inline-block" }}
            >
              Set an Anthropic API key above to use {executionMode} mode.
            </span>
          )}
        </>
      ) : (
        <div className="oa-msg" style={{ marginTop: 4 }}>
          Agentic / Hybrid require the Claude provider. With{" "}
          {PROVIDER_LABEL[defaultProvider]} selected, steps run in single-shot
          mode.
        </div>
      )}
    </div>
  );
}

/* --------------------------- API Credentials --------------------------- */

function CredRow({
  label,
  hint,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div className="oa-cred-row">
      <div>
        <div className="oa-label">{label}</div>
        {hint && <div className="oa-label-hint">{hint}</div>}
      </div>
      <input
        className="oa-input"
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/* --------------------- Confluence connections tab --------------------- */

const BLANK_PROFILE: ConfluenceProfileInput = {
  name: "",
  siteUrl: "",
  userEmail: "",
  apiToken: "",
  confluenceSpace: "",
  jiraProject: "",
};

function ConnectionsTab({
  flash,
}: {
  flash: (kind: "ok" | "err", text: string) => void;
}) {
  const activeEnterpriseId = useStore((s) => s.activeEnterpriseId);
  const [profiles, setProfiles] = useState<ConfluenceProfile[]>([]);
  const [loading, setLoading] = useState(true);
  // null = no editor open; '' marks the "new connection" form; otherwise the id being edited.
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setProfiles(await listConfluenceProfiles());
    } catch (e) {
      flash(
        "err",
        e instanceof AuthError ? e.message : "Failed to load connections.",
      );
    } finally {
      setLoading(false);
    }
  }, [flash]);

  useEffect(() => {
    if (!activeEnterpriseId) {
      setLoading(false);
      return;
    }
    void load();
  }, [load, activeEnterpriseId]);

  const onSaved = async (): Promise<void> => {
    setEditingId(null);
    await load();
  };

  const makeDefault = async (id: string): Promise<void> => {
    try {
      setProfiles(await setDefaultConfluenceProfile(id));
      flash("ok", "Default connection updated.");
    } catch (e) {
      flash(
        "err",
        e instanceof AuthError ? e.message : "Could not set default.",
      );
    }
  };

  const remove = async (p: ConfluenceProfile): Promise<void> => {
    if (
      !window.confirm(
        `Delete connection "${p.name}"? Apps still pointed at it will fall back to the default.`,
      )
    )
      return;
    try {
      await deleteConfluenceProfile(p.id);
      flash("ok", "Connection deleted.");
      await load();
    } catch (e) {
      flash("err", e instanceof AuthError ? e.message : "Delete failed.");
    }
  };

  return (
    <>
      <p className="oa-sub" style={{ marginBottom: 18 }}>
        Confluence connections for this workspace. Add more than one to publish
        different apps to different Confluence sites or spaces; each app picks
        its connection on the app screen, falling back to the default below.
        Tokens are stored encrypted and never shown again.
      </p>

      {loading ? (
        <div style={{ color: "var(--oa-muted)", padding: 20 }}>Loading…</div>
      ) : (
        <div className="oa-section">
          <div
            className="oa-section-head"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Connections</span>
            {editingId === null && (
              <button
                className="oa-btn oa-btn--primary"
                onClick={() => setEditingId("")}
              >
                + Add connection
              </button>
            )}
          </div>

          {editingId === "" && (
            <ConnectionEditor
              key="new"
              initial={BLANK_PROFILE}
              isFirst={profiles.length === 0}
              onCancel={() => setEditingId(null)}
              onSaved={onSaved}
              flash={flash}
            />
          )}

          {profiles.length === 0 && editingId === null && (
            <div
              style={{
                color: "var(--oa-muted)",
                padding: "14px 2px",
                fontSize: 13,
              }}
            >
              No connections yet. Add one to enable Confluence publishing for
              this workspace.
            </div>
          )}

          {profiles.map((p) =>
            editingId === p.id ? (
              <ConnectionEditor
                key={p.id}
                profileId={p.id}
                initial={{
                  name: p.name,
                  siteUrl: p.siteUrl,
                  userEmail: p.userEmail,
                  confluenceSpace: p.confluenceSpace,
                  jiraProject: p.jiraProject,
                  apiToken: "",
                }}
                hasToken={p.hasToken}
                isFirst={false}
                onCancel={() => setEditingId(null)}
                onSaved={onSaved}
                flash={flash}
              />
            ) : (
              <div
                key={p.id}
                className="oa-cred-row"
                style={{ alignItems: "center" }}
              >
                <div>
                  <div
                    className="oa-label"
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    {p.name}
                    {p.isDefault && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "var(--oa-accent, #1a73e8)",
                          border: "1px solid currentColor",
                          borderRadius: 4,
                          padding: "0 6px",
                        }}
                      >
                        Default
                      </span>
                    )}
                  </div>
                  <div className="oa-label-hint">
                    {p.siteUrl || "(no URL)"} · space {p.confluenceSpace || "—"}
                    {p.jiraProject ? ` · Jira ${p.jiraProject}` : ""} ·{" "}
                    {p.hasToken ? "token set" : "no token"}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    justifyContent: "flex-end",
                  }}
                >
                  {!p.isDefault && (
                    <button
                      className="oa-btn"
                      onClick={() => void makeDefault(p.id)}
                    >
                      Set default
                    </button>
                  )}
                  <button className="oa-btn" onClick={() => setEditingId(p.id)}>
                    Edit
                  </button>
                  <button className="oa-btn" onClick={() => void remove(p)}>
                    Delete
                  </button>
                </div>
              </div>
            ),
          )}
        </div>
      )}
    </>
  );
}

function ConnectionEditor({
  profileId,
  initial,
  hasToken,
  isFirst,
  onCancel,
  onSaved,
  flash,
}: {
  profileId?: string;
  initial: ConfluenceProfileInput;
  hasToken?: boolean;
  isFirst: boolean;
  onCancel: () => void;
  onSaved: () => Promise<void> | void;
  flash: (kind: "ok" | "err", text: string) => void;
}) {
  const [form, setForm] = useState<ConfluenceProfileInput>(initial);
  const [busy, setBusy] = useState(false);
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(
    null,
  );
  const set = (k: keyof ConfluenceProfileInput) => (v: string) =>
    setForm((p) => ({ ...p, [k]: v }));

  const test = async (): Promise<void> => {
    setBusy(true);
    setTestMsg(null);
    try {
      const r = await testConfluenceProfile({
        id: profileId,
        siteUrl: form.siteUrl || undefined,
        userEmail: form.userEmail || undefined,
        apiToken: form.apiToken || undefined,
      });
      setTestMsg({ ok: r.ok, text: r.message });
    } finally {
      setBusy(false);
    }
  };

  const save = async (): Promise<void> => {
    if (!form.name?.trim()) {
      flash("err", "Give the connection a name.");
      return;
    }
    setBusy(true);
    try {
      if (profileId) {
        // Don't send a blank token — that would be read as "keep existing".
        const patch = { ...form };
        if (!patch.apiToken) delete patch.apiToken;
        await updateConfluenceProfile(profileId, patch);
        flash("ok", "Connection updated.");
      } else {
        await createConfluenceProfile(form);
        flash("ok", "Connection added.");
      }
      await onSaved();
    } catch (e) {
      flash("err", e instanceof AuthError ? e.message : "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        border: "1px solid var(--line)",
        borderRadius: "var(--r-3)",
        padding: 14,
        margin: "10px 0",
        background: "var(--surface-2)",
      }}
    >
      <CredRow
        label="Name"
        hint="A label for this connection, e.g. Engineering"
        value={form.name}
        onChange={set("name")}
        placeholder="Engineering Confluence"
      />
      <CredRow
        label="Base URL"
        hint="Your Cloud Base URL"
        value={form.siteUrl || ""}
        onChange={set("siteUrl")}
        placeholder="https://yoursite.atlassian.net/"
      />
      <CredRow
        label="Account Email"
        hint="Used as Basic Auth username"
        value={form.userEmail || ""}
        onChange={set("userEmail")}
        placeholder="admin@example.com"
      />
      <CredRow
        label="API Token"
        hint={
          profileId && hasToken
            ? "Leave blank to keep the saved token"
            : "Atlassian API token"
        }
        type="password"
        value={form.apiToken || ""}
        onChange={set("apiToken")}
        placeholder={
          profileId && hasToken ? "•••••••••• (unchanged)" : "ATATT…"
        }
      />
      <CredRow
        label="Confluence Space"
        hint="Space key for artifact publishing"
        value={form.confluenceSpace || ""}
        onChange={set("confluenceSpace")}
        placeholder="RMSIS"
      />
      <CredRow
        label="Jira Project"
        hint="Optional project key for issue publishing"
        value={form.jiraProject || ""}
        onChange={set("jiraProject")}
        placeholder="APP"
      />
      {!profileId && !isFirst && (
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            margin: "6px 2px",
          }}
        >
          <input
            type="checkbox"
            checked={!!form.isDefault}
            onChange={(e) =>
              setForm((p) => ({ ...p, isDefault: e.target.checked }))
            }
          />
          Make this the workspace default
        </label>
      )}
      {!profileId && isFirst && (
        <div
          style={{ fontSize: 12, color: "var(--oa-muted)", margin: "6px 2px" }}
        >
          This will be the workspace's default connection.
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginTop: 10,
        }}
      >
        <button
          className="oa-btn oa-btn--primary"
          disabled={busy}
          onClick={() => void save()}
        >
          {busy ? "Saving…" : "Save"}
        </button>
        <button className="oa-btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        <button className="oa-btn" disabled={busy} onClick={() => void test()}>
          {busy ? "Testing…" : "Test Connection"}
        </button>
        {testMsg && (
          <span
            className={`oa-msg ${testMsg.ok ? "oa-msg--ok" : "oa-msg--err"}`}
          >
            {testMsg.ok ? "✓ " : "✗ "}
            {testMsg.text}
          </span>
        )}
      </div>
    </div>
  );
}

/* --------------------- Git host connections --------------------- */

const BLANK_REPO_PROFILE: RepoProfileInput = {
  name: "",
  host: "github",
  baseUrl: "",
  workspace: "",
  repo: "",
  defaultBranch: "main",
  syncBranch: "",
  username: "",
  authToken: "",
};

function RepoConnectionsTab({
  flash,
}: {
  flash: (kind: "ok" | "err", text: string) => void;
}) {
  const activeEnterpriseId = useStore((s) => s.activeEnterpriseId);
  const [profiles, setProfiles] = useState<RepoProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setProfiles(await listRepoProfiles());
    } catch (e) {
      flash(
        "err",
        e instanceof AuthError ? e.message : "Failed to load git connections.",
      );
    } finally {
      setLoading(false);
    }
  }, [flash]);

  useEffect(() => {
    if (!activeEnterpriseId) {
      setLoading(false);
      return;
    }
    void load();
  }, [load, activeEnterpriseId]);

  const onSaved = async (): Promise<void> => {
    setEditingId(null);
    await load();
  };

  const makeDefault = async (id: string): Promise<void> => {
    try {
      setProfiles(await setDefaultRepoProfile(id));
      flash("ok", "Default git connection updated.");
    } catch (e) {
      flash(
        "err",
        e instanceof AuthError ? e.message : "Could not set default.",
      );
    }
  };

  const remove = async (p: RepoProfile): Promise<void> => {
    if (
      !window.confirm(
        `Delete git connection "${p.name}"? Apps still pointed at it will fall back to the default.`,
      )
    )
      return;
    try {
      await deleteRepoProfile(p.id);
      flash("ok", "Git connection deleted.");
      await load();
    } catch (e) {
      flash("err", e instanceof AuthError ? e.message : "Delete failed.");
    }
  };

  return (
    <>
      <p className="oa-sub" style={{ margin: "28px 0 18px" }}>
        GitHub &amp; Bitbucket connections for this workspace. Add one to sync
        an app's whole tree (code, docs and manifest) to a repository; each app
        picks its connection on the app screen, falling back to the default
        below. Tokens are stored encrypted and never shown again.
      </p>

      {loading ? (
        <div style={{ color: "var(--oa-muted)", padding: 20 }}>Loading…</div>
      ) : (
        <div className="oa-section">
          <div
            className="oa-section-head"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Git connections</span>
            {editingId === null && (
              <button
                className="oa-btn oa-btn--primary"
                onClick={() => setEditingId("")}
              >
                + Add connection
              </button>
            )}
          </div>

          {editingId === "" && (
            <RepoConnectionEditor
              key="new"
              initial={BLANK_REPO_PROFILE}
              isFirst={profiles.length === 0}
              onCancel={() => setEditingId(null)}
              onSaved={onSaved}
              flash={flash}
            />
          )}

          {profiles.length === 0 && editingId === null && (
            <div
              style={{
                color: "var(--oa-muted)",
                padding: "14px 2px",
                fontSize: 13,
              }}
            >
              No git connections yet. Add one to enable GitHub sync for this
              workspace.
            </div>
          )}

          {profiles.map((p) =>
            editingId === p.id ? (
              <RepoConnectionEditor
                key={p.id}
                profileId={p.id}
                initial={{
                  name: p.name,
                  host: p.host,
                  baseUrl: p.baseUrl,
                  workspace: p.workspace,
                  repo: p.repo,
                  defaultBranch: p.defaultBranch,
                  syncBranch: p.syncBranch,
                  username: p.username,
                  authToken: "",
                }}
                hasToken={p.hasToken}
                isFirst={false}
                onCancel={() => setEditingId(null)}
                onSaved={onSaved}
                flash={flash}
              />
            ) : (
              <div
                key={p.id}
                className="oa-cred-row"
                style={{ alignItems: "center" }}
              >
                <div>
                  <div
                    className="oa-label"
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    {p.name}
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "var(--oa-muted)",
                        border: "1px solid currentColor",
                        borderRadius: 4,
                        padding: "0 6px",
                      }}
                    >
                      {p.host}
                    </span>
                    {p.isDefault && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "var(--oa-accent, #1a73e8)",
                          border: "1px solid currentColor",
                          borderRadius: 4,
                          padding: "0 6px",
                        }}
                      >
                        Default
                      </span>
                    )}
                  </div>
                  <div className="oa-label-hint">
                    {p.workspace || "(no workspace)"}/
                    {p.repo || "(app-derived)"} · branch{" "}
                    {p.syncBranch || p.defaultBranch} ·{" "}
                    {p.hasToken ? "token set" : "no token"}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    justifyContent: "flex-end",
                  }}
                >
                  {!p.isDefault && (
                    <button
                      className="oa-btn"
                      onClick={() => void makeDefault(p.id)}
                    >
                      Set default
                    </button>
                  )}
                  <button className="oa-btn" onClick={() => setEditingId(p.id)}>
                    Edit
                  </button>
                  <button className="oa-btn" onClick={() => void remove(p)}>
                    Delete
                  </button>
                </div>
              </div>
            ),
          )}
        </div>
      )}
    </>
  );
}

function RepoConnectionEditor({
  profileId,
  initial,
  hasToken,
  isFirst,
  onCancel,
  onSaved,
  flash,
}: {
  profileId?: string;
  initial: RepoProfileInput;
  hasToken?: boolean;
  isFirst: boolean;
  onCancel: () => void;
  onSaved: () => Promise<void> | void;
  flash: (kind: "ok" | "err", text: string) => void;
}) {
  const [form, setForm] = useState<RepoProfileInput>(initial);
  const [busy, setBusy] = useState(false);
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(
    null,
  );
  const set = (k: keyof RepoProfileInput) => (v: string) =>
    setForm((p) => ({ ...p, [k]: v }));

  const test = async (): Promise<void> => {
    setBusy(true);
    setTestMsg(null);
    try {
      const r = await testRepoProfile({
        id: profileId,
        host: form.host,
        baseUrl: form.baseUrl || undefined,
        workspace: form.workspace || undefined,
        repo: form.repo || undefined,
        username: form.username || undefined,
        authToken: form.authToken || undefined,
      });
      setTestMsg({ ok: r.ok, text: r.message });
    } finally {
      setBusy(false);
    }
  };

  const save = async (): Promise<void> => {
    if (!form.name?.trim()) {
      flash("err", "Give the connection a name.");
      return;
    }
    setBusy(true);
    try {
      if (profileId) {
        const patch = { ...form };
        if (!patch.authToken) delete patch.authToken;
        await updateRepoProfile(profileId, patch);
        flash("ok", "Git connection updated.");
      } else {
        await createRepoProfile(form);
        flash("ok", "Git connection added.");
      }
      await onSaved();
    } catch (e) {
      flash("err", e instanceof AuthError ? e.message : "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const isBitbucket = form.host === "bitbucket";

  return (
    <div
      style={{
        border: "1px solid var(--line)",
        borderRadius: "var(--r-3)",
        padding: 14,
        margin: "10px 0",
        background: "var(--surface-2)",
      }}
    >
      <CredRow
        label="Name"
        hint="A label for this connection, e.g. Engineering GitHub"
        value={form.name}
        onChange={set("name")}
        placeholder="Engineering GitHub"
      />
      <div className="oa-cred-row">
        <div>
          <div className="oa-label">Host</div>
          <div className="oa-label-hint">GitHub or Bitbucket</div>
        </div>
        <select
          className="oa-input"
          value={form.host}
          onChange={(e) =>
            setForm((p) => ({ ...p, host: e.target.value as RepoHost }))
          }
        >
          <option value="github">GitHub</option>
          <option value="bitbucket">Bitbucket</option>
        </select>
      </div>
      <CredRow
        label="Workspace / Owner"
        hint={isBitbucket ? "Bitbucket workspace slug" : "GitHub user or org"}
        value={form.workspace || ""}
        onChange={set("workspace")}
        placeholder="my-org"
      />
      <CredRow
        label="Repository"
        hint="Leave blank to derive from the app name"
        value={form.repo || ""}
        onChange={set("repo")}
        placeholder="my-app"
      />
      <CredRow
        label="Default Branch"
        hint="Base branch (e.g. main)"
        value={form.defaultBranch || ""}
        onChange={set("defaultBranch")}
        placeholder="main"
      />
      <CredRow
        label="Sync Branch"
        hint="Blank = commit straight to the default branch"
        value={form.syncBranch || ""}
        onChange={set("syncBranch")}
        placeholder="app-factory-sync"
      />
      {isBitbucket && (
        <CredRow
          label="Atlassian Email"
          hint="Your Atlassian account email — the username for API-token Basic auth"
          value={form.username || ""}
          onChange={set("username")}
          placeholder="you@example.com"
        />
      )}
      <CredRow
        label={isBitbucket ? "API Token" : "Access Token (PAT)"}
        hint={
          profileId && hasToken
            ? "Leave blank to keep the saved token"
            : isBitbucket
              ? "Atlassian API token (app passwords are being removed)"
              : "GitHub personal access token"
        }
        type="password"
        value={form.authToken || ""}
        onChange={set("authToken")}
        placeholder={
          profileId && hasToken
            ? "•••••••••• (unchanged)"
            : isBitbucket
              ? "ATATT…"
              : "ghp_…"
        }
      />
      {isBitbucket && (
        <div className="oa-label-hint" style={{ margin: "2px 2px 8px" }}>
          Bitbucket app passwords are deprecated (removed 2026-07-28). Create an
          API token at{" "}
          <a
            href="https://id.atlassian.com/manage-profile/security/api-tokens"
            target="_blank"
            rel="noreferrer"
          >
            id.atlassian.com → API tokens
          </a>{" "}
          and use it with your account email above.
        </div>
      )}
      <CredRow
        label="API Base URL"
        hint="Optional — for GitHub Enterprise / Bitbucket Server"
        value={form.baseUrl || ""}
        onChange={set("baseUrl")}
        placeholder="(host default)"
      />
      {!profileId && !isFirst && (
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            margin: "6px 2px",
          }}
        >
          <input
            type="checkbox"
            checked={!!form.isDefault}
            onChange={(e) =>
              setForm((p) => ({ ...p, isDefault: e.target.checked }))
            }
          />
          Make this the workspace default
        </label>
      )}
      {!profileId && isFirst && (
        <div
          style={{ fontSize: 12, color: "var(--oa-muted)", margin: "6px 2px" }}
        >
          This will be the workspace's default git connection.
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginTop: 10,
        }}
      >
        <button
          className="oa-btn oa-btn--primary"
          disabled={busy}
          onClick={() => void save()}
        >
          {busy ? "Saving…" : "Save"}
        </button>
        <button className="oa-btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        <button className="oa-btn" disabled={busy} onClick={() => void test()}>
          {busy ? "Testing…" : "Test Connection"}
        </button>
        {testMsg && (
          <span
            className={`oa-msg ${testMsg.ok ? "oa-msg--ok" : "oa-msg--err"}`}
          >
            {testMsg.ok ? "✓ " : "✗ "}
            {testMsg.text}
          </span>
        )}
      </div>
    </div>
  );
}

/* --------------------------- Jira DC Connector section --------------------------- */

function DcConnectorSection({
  flash,
}: {
  flash: (kind: "ok" | "err", text: string) => void;
}) {
  const [config, setConfig] = useState<ConfigMap>({});
  const [dcUrl, setDcUrl] = useState("");
  const [dcPat, setDcPat] = useState("");
  const [busy, setBusy] = useState(false);
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    void getAdminConfig().then((r) => {
      setConfig(r.config);
      setDcUrl(r.config["JIRA_DC_URL"]?.value || "");
    });
  }, []);

  const patSet = config["JIRA_DC_PAT"]?.isSet;

  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      await saveAdminConfig({ JIRA_DC_URL: dcUrl, ...(dcPat ? { JIRA_DC_PAT: dcPat } : {}) });
      flash("ok", "DC credentials saved.");
      setDcPat("");
      const r = await getAdminConfig();
      setConfig(r.config);
    } catch (e) {
      flash("err", e instanceof AuthError ? e.message : "Failed to save.");
    } finally {
      setBusy(false);
    }
  };

  const test = async (): Promise<void> => {
    setBusy(true);
    setTestMsg(null);
    try {
      const r = await testDcConnection({
        JIRA_DC_URL: dcUrl || undefined,
        JIRA_DC_PAT: dcPat || undefined,
      });
      setTestMsg({ ok: r.ok, text: r.ok && r.version ? `${r.message} (Jira ${r.version})` : r.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>Jira Data Center Connector</div>
      <p style={{ fontSize: 12, color: "var(--ink-muted)", margin: "0 0 12px" }}>
        Connect to your Jira DC instance so migration bundles can read real data — projects, users,
        apps, workflows, custom fields, and issue counts — instead of relying on assumptions.
        Create a Personal Access Token in Jira DC under <em>Profile → Personal Access Tokens</em>.
      </p>
      <CredRow
        label="DC Base URL"
        hint="Your Jira DC instance URL (https:// required for non-localhost)"
        value={dcUrl}
        onChange={setDcUrl}
        placeholder="https://jira.company.com:8080"
      />
      <CredRow
        label="Personal Access Token"
        hint="PAT with read access (DC 8.14+)"
        type="password"
        value={dcPat}
        onChange={setDcPat}
        placeholder={patSet ? config["JIRA_DC_PAT"].value : "Paste PAT here…"}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
        <button className="oa-btn" disabled={busy} onClick={() => void test()}>
          {busy ? "Testing…" : "Test Connection"}
        </button>
        <button className="oa-btn oa-btn--primary" disabled={busy} onClick={() => void save()}>
          Save
        </button>
        {testMsg && (
          <span className={`oa-msg ${testMsg.ok ? "oa-msg--ok" : "oa-msg--err"}`}>
            {testMsg.ok ? "✓ " : "✗ "}{testMsg.text}
          </span>
        )}
      </div>
    </div>
  );
}

/* --------------------------- Email tab --------------------------- */

function EmailTab({
  smtp,
  setSmtp,
  config,
}: {
  smtp: Record<string, string>;
  setSmtp: (f: (p: Record<string, string>) => Record<string, string>) => void;
  config: ConfigMap;
}) {
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: string) => (v: string) => setSmtp((p) => ({ ...p, [k]: v }));
  const passSet = config["SMTP_PASSWORD"]?.isSet;

  const test = async (): Promise<void> => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await testSmtp();
      setMsg({ ok: r.ok, text: r.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <p className="oa-sub" style={{ marginBottom: 18 }}>
        SMTP settings for outbound email delivery. Used for password reset
        emails and notifications.
      </p>
      <div className="oa-section">
        <div className="oa-section-head">SMTP / Email Delivery</div>
        <CredRow
          label="SMTP Host"
          value={smtp.SMTP_HOST}
          onChange={set("SMTP_HOST")}
          placeholder="smtp.gmail.com"
        />
        <CredRow
          label="SMTP Port"
          value={smtp.SMTP_PORT}
          onChange={set("SMTP_PORT")}
          placeholder="587"
        />
        <CredRow
          label="SMTP Username"
          value={smtp.SMTP_USERNAME}
          onChange={set("SMTP_USERNAME")}
          placeholder="noreply@example.com"
        />
        <CredRow
          label="SMTP Password"
          type="password"
          value={smtp.SMTP_PASSWORD}
          onChange={set("SMTP_PASSWORD")}
          placeholder={
            passSet
              ? config["SMTP_PASSWORD"].value
              : "App password or SMTP password"
          }
        />
        <CredRow
          label="From Address"
          hint="Sender shown on outbound mail"
          value={smtp.SMTP_FROM}
          onChange={set("SMTP_FROM")}
          placeholder="App Factory <no-reply@example.com>"
        />
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button
            className="oa-btn"
            disabled={busy}
            onClick={() => void test()}
          >
            {busy ? "Testing…" : "Test Connection"}
          </button>
          {msg && (
            <span className={`oa-msg ${msg.ok ? "oa-msg--ok" : "oa-msg--err"}`}>
              {msg.ok ? "✓ " : "✗ "}
              {msg.text}
            </span>
          )}
        </div>
      </div>
    </>
  );
}
